import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Cake, Gift, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export default function Friends() {
  const { t } = useLang();
  const [friends, setFriends] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const invite = async () => {
    if (!inviteEmail.includes("@")) { toast.error(t("invalid_email")); return; }
    setInviting(true);
    try {
      await api.post("/invite", { email: inviteEmail });
      toast.success(t("invite_sent"));
      setInviteEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("invite_failed"));
    } finally { setInviting(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/friends");
      setFriends(res.data);
    } catch (err) {
      console.error("friends load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/users-search", { params: { q: query } });
        setResults(res.data);
      } catch (err) {
        console.error("user search failed", err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const thisWeek = friends?.filter((f) => f.days_to_birthday !== null && f.days_to_birthday <= 7) || [];

  const UserRow = ({ u, showBirthday }) => (
    <Link to={`/${u.username}`} data-testid={`friend-row-${u.username}`}
      className="flex items-center gap-3 bg-card rounded-2xl border border-border/50 p-4 card-lift">
      <Avatar className="w-12 h-12">
        <AvatarImage src={u.picture} />
        <AvatarFallback className="bg-secondary font-semibold">{u.name?.[0]}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{u.name}</p>
        <p className="text-xs text-muted-foreground">@{u.username}</p>
      </div>
      {showBirthday && u.days_to_birthday !== null && (
        <span className="text-xs text-primary font-semibold flex items-center gap-1 shrink-0">
          <Cake size={13} /> {u.days_to_birthday === 0 ? t("today_party") : `${u.days_to_birthday} ${t("days_short")}`}
        </span>
      )}
      {u.wishlist_count !== undefined && (
        <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><Gift size={13} /> {u.wishlist_count}</span>
      )}
    </Link>
  );

  return (
    <div data-testid="friends-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{t("friends_title")}</h1>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("friends_search")}
          className="rounded-full pl-11 h-12" data-testid="friends-search-input" />
      </div>

      <div className="bg-secondary/60 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="invite-box">
        <p className="text-sm text-muted-foreground flex-1">{t("invite_text")}</p>
        <div className="flex gap-2">
          <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="arkadas@mail.com" className="rounded-full h-10 w-52" data-testid="invite-email-input" />
          <Button onClick={invite} disabled={inviting} className="rounded-full h-10 shrink-0" data-testid="invite-send-btn">
            <Send size={14} className="mr-1" /> {t("invite_btn")}
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="space-y-3 mb-8">
          <h2 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider">{t("search_results")}</h2>
          {results.map((u) => <UserRow key={u.user_id} u={u} />)}
        </div>
      )}

      {thisWeek.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2"><Cake size={18} className="text-primary" /> {t("bday_week")}</h2>
          <div className="space-y-3">
            {thisWeek.map((u) => <UserRow key={u.user_id} u={u} showBirthday />)}
          </div>
        </div>
      )}

      <h2 className="font-heading text-lg font-semibold mb-3">{t("following_title")}</h2>
      {!friends ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : friends.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground">
          {t("no_friends")}
        </div>
      ) : (
        <div className="space-y-3">
          {friends.map((u) => <UserRow key={u.user_id} u={u} showBirthday />)}
        </div>
      )}
    </div>
  );
}
